import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, Flower2, BookOpen, Camera, Users } from "lucide-react";
import CommunityLayout from "@/components/community/CommunityLayout";
import { EventCard, BulletinCard, SectionTitle } from "@/components/community/Cards";
import { VideoEmbed } from "@/components/community/VideoEmbed";
import { cApi, img, STOCK, DEFAULT_CONTENT } from "@/lib/community";

const BENEFITS = [
  { icon: Heart, title: "Sisterhood", body: "Real friendships with women who cheer for you, pray for you and show up." },
  { icon: Flower2, title: "Wellness", body: "Pilates mornings, self-care days and rituals that restore body and soul." },
  { icon: BookOpen, title: "Faith & Growth", body: "Bible studies and honest conversations about becoming who God called you to be." },
  { icon: Camera, title: "Memories", body: "Photoshoots, brunches and sip & paint nights worth framing." },
  { icon: Sparkles, title: "Opportunities", body: "Networking, business education and doors opened for each other." },
  { icon: Users, title: "Belonging", body: "A weekly bulletin, a community wall and a place that feels like home." },
];

function Hero({ c }) {
  return (
    <section className="relative min-h-[78vh] flex items-end overflow-hidden jc-grain" data-testid="experience-hero">
      <img src={img(c.hero_image) || STOCK.hero} alt="Jenova girls together" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2B2522]/80 via-[#2B2522]/40 to-[#2B2522]/20" />
      <div className="relative max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-16 pb-14 md:pb-20">
        <p className="jc-eyebrow !text-[#F3E1DC] [text-shadow:0_1px_12px_rgba(43,37,34,0.6)] jc-fade jc-d1">The Jenova Girls Experience</p>
        <h1 data-testid="experience-headline" className="jc-display text-[#FBF8F3] text-5xl sm:text-6xl lg:text-7xl mt-5 max-w-5xl uppercase jc-fade jc-d2">
          {c.hero_headline}
        </h1>
        <p className="mt-7 text-[#FBF8F3]/85 max-w-xl text-base md:text-lg leading-relaxed font-light jc-fade jc-d3">{c.hero_description}</p>
        <div className="mt-10 flex flex-wrap gap-3 jc-fade jc-d4">
          <Link to="/become-a-jenova-girl" data-testid="hero-become-btn" className="jc-btn jc-btn-blush">
            Become a Jenova Girl <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
          </Link>
          <Link to="/bulletin" data-testid="hero-explore-week-btn" className="jc-btn jc-btn-light">Explore This Week</Link>
        </div>
      </div>
    </section>
  );
}

function Founder({ c }) {
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36 grid md:grid-cols-12 gap-12 md:gap-16 items-center" data-testid="founder-section">
      <div className="md:col-span-6 lg:col-span-5">
        {c.founder_video_url ? (
          <VideoEmbed url={img(c.founder_video_url)} poster={img(c.founder_image)} />
        ) : (
          <div className="jc-card-img aspect-[4/5]">
            <img src={img(c.founder_image) || STOCK.founder} alt={c.founder_name} className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      <div className="md:col-span-6 lg:col-start-7">
        <p className="jc-eyebrow">A welcome from our founder</p>
        <div className="mt-6 space-y-5 text-[#2B2522]" data-testid="founder-message">
          {c.founder_message.split(/\n\s*\n/).map((para, i) => (
            <p key={i} className={i === 0 ? "jc-display italic text-4xl md:text-5xl leading-[1.1]" : "font-serif text-xl md:text-2xl leading-[1.5] text-[#4A403B]"}>
              {para}
            </p>
          ))}
        </div>
        <div className="mt-10 flex items-center gap-4">
          <span className="w-12 h-px bg-[#C9A97A]" />
          <div>
            <p className="font-serif text-xl">{c.founder_name}</p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A6A62] mt-1">{c.founder_title}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Intro({ c }) {
  return (
    <section className="bg-[#F3E1DC]/60">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <SectionTitle eyebrow="The community" title={c.intro_title} />
        </div>
        <div className="md:col-span-6 md:col-start-7 flex flex-col justify-end">
          <p className="text-base md:text-lg leading-relaxed text-[#4A403B]" data-testid="intro-body">{c.intro_body}</p>
          <Link to="/become-a-jenova-girl" className="mt-8 self-start text-[11px] uppercase tracking-[0.24em] border-b border-[#2B2522] pb-1 hover:text-[#A8865A] hover:border-[#A8865A] transition-colors" data-testid="intro-join-link">
            What it means to be a Jenova Girl
          </Link>
        </div>
      </div>
    </section>
  );
}

function Memories({ photos }) {
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32" data-testid="memories-section">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionTitle eyebrow="Moments we've shared" title="Memories from" em="previous experiences" />
        <Link to="/community-wall" className="jc-btn jc-btn-ghost" data-testid="memories-wall-btn">Visit the Community Wall</Link>
      </div>
      <div className="mt-14 grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-5">
        {photos.slice(0, 6).map((p, i) => (
          <div
            key={i}
            className={`jc-card-img ${["md:col-span-5 aspect-[4/5]", "md:col-span-4 aspect-[3/4] md:mt-16", "md:col-span-3 aspect-[3/4]",
              "md:col-span-3 aspect-square", "md:col-span-4 aspect-[4/3]", "md:col-span-5 aspect-[16/10]"][i] || "aspect-square"}`}
          >
            <img src={img(p.url || p)} alt={p.caption || "Jenova memory"} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section className="bg-[#F5EFE6]" data-testid="benefits-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <SectionTitle eyebrow="Why women join" title="What the sisterhood" em="gives you" />
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8DCCB]">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-[#F5EFE6] p-8 md:p-12 group hover:bg-[#FBF8F3] transition-colors duration-500">
              <b.icon className="w-6 h-6 text-[#A8865A]" strokeWidth={1} />
              <h3 className="jc-display text-3xl mt-6">{b.title}</h3>
              <p className="mt-3 text-sm text-[#7A6A62] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const CACHE_KEY = "jc_experience_v1";
const readCache = () => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)); } catch { return null; }
};

export default function Experience() {
  const cached = readCache();
  const [c, setC] = useState(cached?.c || null);
  const [events, setEvents] = useState(cached?.events || []);
  const [bulletin, setBulletin] = useState(cached?.bulletin || []);
  const [photos, setPhotos] = useState(cached?.photos || null);

  useEffect(() => {
    Promise.all([
      cApi.get("/community/content").then(({ data }) => ({ ...DEFAULT_CONTENT, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== "" && v != null)) })).catch(() => DEFAULT_CONTENT),
      cApi.get("/community/events").then(({ data }) => data.items.slice(0, 3)).catch(() => []),
      cApi.get("/community/bulletin?limit=3").then(({ data }) => data.items).catch(() => []),
      cApi.get("/community/gallery?featured=true").then(({ data }) => data.photos).catch(() => []),
    ]).then(([content, ev, bu, ph]) => {
      setC(content); setEvents(ev); setBulletin(bu); setPhotos(ph);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ c: content, events: ev, bulletin: bu, photos: ph })); } catch { /* storage full */ }
    });
  }, []);

  if (!c || !photos) {
    return (
      <CommunityLayout>
        <div className="min-h-[80vh] flex items-center justify-center" data-testid="experience-loading">
          <p className="jc-eyebrow animate-pulse">The Jenova Girls Experience</p>
        </div>
      </CommunityLayout>
    );
  }

  const memoryPhotos = photos.length >= 3 ? photos : (c.memory_images?.length ? c.memory_images : STOCK.memories);

  return (
    <CommunityLayout>
      <Hero c={c} />
      <Founder c={c} />
      <Intro c={c} />
      <Memories photos={memoryPhotos} />
      <Benefits />
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32" data-testid="upcoming-preview">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionTitle eyebrow="Jenova Experiences" title="Upcoming" em="activities" />
          <Link to="/events" className="jc-btn jc-btn-ghost" data-testid="upcoming-all-btn">All experiences</Link>
        </div>
        {events.length ? (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {events.map((e, i) => <EventCard key={e.id} event={e} idx={i} />)}
          </div>
        ) : (
          <p className="mt-10 jc-display italic text-2xl text-[#A8865A]">New experiences are being planned — check back soon.</p>
        )}
      </section>
      {bulletin.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32" data-testid="bulletin-preview">
          <SectionTitle eyebrow="Weekly Bulletin" title="This week at" em="Jenova" />
          <div className="mt-14 grid md:grid-cols-3 gap-10">
            {bulletin.map((b) => <BulletinCard key={b.id} item={b} />)}
          </div>
        </section>
      )}
      <section className="relative overflow-hidden jc-grain">
        <img src={STOCK.sisterhood} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2B2522]/55" />
        <div className="relative max-w-3xl mx-auto px-6 py-28 md:py-40 text-center">
          <p className="jc-eyebrow !text-[#F3E1DC]">Your seat is waiting</p>
          <h2 className="jc-display text-[#FBF8F3] text-4xl sm:text-5xl lg:text-6xl mt-5">Come as you are. <em className="italic">Leave with sisters.</em></h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/become-a-jenova-girl" className="jc-btn jc-btn-blush" data-testid="cta-become-btn">Become a Jenova Girl</Link>
            <Link to="/prive" className="jc-btn jc-btn-light" data-testid="cta-prive-btn">Discover Privé Club</Link>
          </div>
        </div>
      </section>
    </CommunityLayout>
  );
}
